import { graphql, useStaticQuery } from "gatsby";
import PropTypes from "prop-types";
import React, { useRef, useState } from "react";
import SwiperCore from "swiper";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import MountainIllustrationGray from "images/illustrations/Mountain_illustration_gray.inline.svg";
import MountainIllustration from "images/illustrations/Mountain_illustration.inline.svg";
import DarkIllustration from "images/illustrations/program-tabs-ill-dark.inline.svg";

import { IssueSlide } from "./issueSlide";

import "swiper/css/bundle";
import "swiper/css/navigation";

// install Swiper components
SwiperCore.use([Autoplay, Navigation, Pagination]);

const IssueSlider = ({ customSectionTitle, customSlides }) => {
  const { page } = useStaticQuery(graphql`
    query {
      page: wpPage(slug: { eq: "home-page" }) {
        ...HomepageIssuesWeCareAboutFragment
      }
    }
  `);

  const issuesList = page?.acfIssuesWeCareAbout;
  const sectionTitle =
    customSectionTitle?.length > 3
      ? customSectionTitle
      : issuesList?.iwcaSectionTitle;
  const slides =
    customSlides?.length > 0
      ? customSlides
      : issuesList?.iwcaSlides.map((slide) => ({
          title: slide.iwcaTitle,
          subtitle: slide.iwcaSubtitle,
          content: slide.iwcaDesc,
          images: slide.iwcaImages ?? [],
          bg_image: slide.iwcaBgImage,
          cta_title: slide.iwcaCtaTitle,
          cta_url: slide.iwcaCtaUrl
        })) ?? [];

  const swiperRef = useRef(null);

  const [, setCurrentSlide] = useState(null); // it will be used to detect feature slide

  return (
    <section
      id="issues-we-care-about-sliders"
      key="issues-we-care-about-key"
      className="!min-h-0"
    >
      <div className="w-full relative overflow-hidden">
        <div className="absolute right-0 top-9 sm:top-20 flex items-center z-30">
          <div
            className="uppercase h7 !text-gray-generic dark:!text-gray-darken pr-5"
            dangerouslySetInnerHTML={{ __html: sectionTitle }}
          />
          <div className="h-2 w-14 md:w-32 rounded-l-full bg-yellow-standard dark:bg-[#4C4C4C]" />
        </div>
        <DarkIllustration className="!absolute w-auto hidden dark:md:block -right-[150px] -bottom-[470px] stroke-black-lighter" />
        <MountainIllustration className="!absolute w-1/2 hidden md:block dark:hidden -right-[18vw] top-[23vw] xl:w-2/5" />
        <MountainIllustrationGray className="!absolute md:hidden dark:hidden -bottom-[24%] -right-[37%] w-[114%] h-[51%]" />
        <div className="issues-swiper-container group w-screen pt-24 pb-6 md:p-0">
          <Swiper
            ref={swiperRef}
            speed={2000}
            grabCursor={true}
            spaceBetween={15}
            pagination={{
              clickable: true
            }}
            loop={true}
            className="z-20"
            onSlideChange={(swiperCore) => {
              const { activeIndex } = swiperCore;
              setCurrentSlide(activeIndex);
            }}
            breakpoints={{
              320: {
                slidesPerView: 1.3,
                spaceBetween: 24,
                navigation: true,
                centeredSlides: true
              },
              720: {
                slidesPerView: 1,
                spaceBetween: 0,
                navigation: true,
                centeredSlides: true
              }
            }}
          >
            {slides.map((issue, index) => (
              <SwiperSlide key={index}>
                <IssueSlide index={index} slideData={issue} />
              </SwiperSlide>
            ))}
          </Swiper>
          <div
            className="swiper-button-prev rotate-180 !left-9 !w-11 h-11 after:hidden !hidden md:!block
            md:opacity-0 md:group-hover:opacity-100 transition-all duration-300"
            onClick={() => swiperRef.current.swiper.slidePrev()}
          />
          <div
            className="swiper-button-next !right-9 !w-11 h-11 after:hidden !hidden md:!block
            md:opacity-0 md:group-hover:opacity-100 transition-all duration-300"
            onClick={() => swiperRef.current.swiper.slideNext()}
          />
        </div>
      </div>
    </section>
  );
};

IssueSlider.defaultProps = {
  customSlides: [],
  customSectionTitle: ""
};

IssueSlider.propTypes = {
  customSlides: PropTypes.array,
  customSectionTitle: PropTypes.string
};
export default IssueSlider;
